import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UserCheck, LogIn, LogOut, Loader2, Camera, ShieldAlert } from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { format } from "date-fns";

// face-api.js is loaded lazily from CDN — on-device only, nothing sent to servers.
const MODELS_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model";
const LIB_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/dist/face-api.min.js";

function loadFaceApi() {
  return new Promise((resolve, reject) => {
    if (window.faceapi) return resolve(window.faceapi);
    const s = document.createElement("script");
    s.src = LIB_URL; s.async = true;
    s.onload = () => resolve(window.faceapi);
    s.onerror = () => reject(new Error("face-api load failed"));
    document.body.appendChild(s);
  });
}

export default function AttendancePage() {
  const { user } = useAuth();
  const canManage = ["admin","super-admin","manager"].includes(user?.role);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [descriptors, setDescriptors] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const load = async () => {
    const [d, l] = await Promise.all([
      api.get("/attendance/descriptors").catch(()=>({data:{items:[]}})),
      api.get("/attendance/log").catch(()=>({data:{items:[]}})),
    ]);
    setDescriptors(d.data.items || []);
    setLog(l.data.items || []);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const faceapi = await loadFaceApi();
        // Silence TF.js WASM warning: ensure tf is ready and prefer webgl backend.
        try {
          if (faceapi.tf?.ready) await faceapi.tf.ready();
          if (faceapi.tf?.setBackend) await faceapi.tf.setBackend("webgl");
        } catch { /* fall back to whatever backend loads */ }
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URL),
        ]);
        if (!cancelled) { setReady(true); setLoading(false); }
      } catch (e) {
        console.error(e);
        toast.error("Face model failed to load. Check network / CDN.");
        setLoading(false);
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, []);

  const startCamera = async () => {
    if (streamRef.current) return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
    } catch (e) { toast.error("Camera unavailable. Grant permission."); }
  };
  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const captureDescriptor = async () => {
    if (!ready || !videoRef.current) return null;
    const faceapi = window.faceapi;
    const det = await faceapi
      .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();
    if (!det) return null;
    return Array.from(det.descriptor);
  };

  const enroll = async () => {
    setBusy(true);
    try {
      await startCamera();
      await new Promise(r => setTimeout(r, 800));
      const desc = await captureDescriptor();
      if (!desc) return toast.error("No face detected. Face the camera and try again.");
      await api.post("/attendance/enroll", { user_id: user.id, descriptor: desc });
      toast.success("Face enrolled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const match = (desc) => {
    if (!desc || descriptors.length === 0) return null;
    const faceapi = window.faceapi;
    const labeled = descriptors.map((r) => new faceapi.LabeledFaceDescriptors(r.user_id, [new Float32Array(r.descriptor)]));
    const matcher = new faceapi.FaceMatcher(labeled, 0.6);
    const best = matcher.findBestMatch(new Float32Array(desc));
    if (best.label === "unknown") return null;
    const user = descriptors.find((r)=>r.user_id === best.label);
    return { user_id: best.label, user_name: user?.user_name, distance: best.distance, confidence: 1 - best.distance };
  };

  const doCheck = async (kind) => {
    setBusy(true);
    try {
      await startCamera();
      await new Promise(r => setTimeout(r, 800));
      const desc = await captureDescriptor();
      if (!desc) return toast.error("No face detected.");
      const found = match(desc);
      if (!found) return toast.error("Face not recognised. Ask admin to enrol you first.");
      await api.post("/attendance/checkin", { user_id: found.user_id, kind, confidence: found.confidence });
      toast.success(`${found.user_name} · ${kind === "in" ? "Checked in" : "Checked out"} (${(found.confidence*100).toFixed(0)}%)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <PageHead
        eyebrow="Operations · HR"
        title="Staff Attendance"
        subtitle="On-device face recognition (face-api.js). No images leave your device — only a 128-D descriptor is stored."
      />

      <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[420px_1fr] gap-4 items-start">
          <div>
            <div className="rounded-xl overflow-hidden bg-black aspect-video relative">
              <video ref={videoRef} playsInline muted className="w-full h-full object-cover"/>
              {!streamRef.current && (
                <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                  {loading ? <Loader2 className="animate-spin" size={24}/> : <Camera size={24}/>}
                </div>
              )}
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Button variant="outline" className="rounded-full h-10" onClick={startCamera}><Camera size={14} className="mr-1"/>Start camera</Button>
              <Button variant="outline" className="rounded-full h-10" onClick={stopCamera}>Stop</Button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6" onClick={()=>doCheck("in")} disabled={busy || !ready} data-testid="check-in-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <LogIn size={14} className="mr-2"/>} Check in
              </Button>
              <Button variant="outline" className="rounded-full border-[#0C6E3D] text-[#0C6E3D] h-11 px-6" onClick={()=>doCheck("out")} disabled={busy || !ready} data-testid="check-out-btn">
                <LogOut size={14} className="mr-2"/> Check out
              </Button>
              <Button variant="outline" className="rounded-full h-11 px-6" onClick={enroll} disabled={busy || !ready} data-testid="enroll-btn">
                <UserCheck size={14} className="mr-2"/> Enrol my face
              </Button>
            </div>
            <div className="text-xs text-[#4A5A50] flex items-center gap-1"><ShieldAlert size={12}/> Privacy: face image never leaves the browser; only a mathematical descriptor is stored.</div>
            <div className="text-xs">{descriptors.length} enrolled · matcher threshold 0.6</div>
          </div>
        </div>
      </CardContent></Card>

      <SectionHead title={canManage ? "Today's log (all)" : "My recent activity"}/>
      {log.length === 0 ? (
        <EmptyState icon={UserCheck} eyebrow="No check-ins yet" title="Enrol first, then check in" description="Click 'Enrol my face' with a clear frontal view, then use Check in / Check out on subsequent days."/>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden"><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <th className="p-3 text-left">When</th>
              <th className="p-3 text-left">User</th>
              <th className="p-3 text-left">Kind</th>
              <th className="p-3 text-left">Confidence</th>
            </tr></thead>
            <tbody>{log.map((e)=>(
              <tr key={e.id} className="border-t border-[#E4DFCF]">
                <td className="p-3 text-xs">{format(new Date(e.created_at), "d MMM HH:mm:ss")}</td>
                <td className="p-3">{e.user_name}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${e.kind==="in"?"bg-[#E1F0E6] text-[#0F5E39]":"bg-[#F6EED8] text-[#6E5416]"}`}>{e.kind}</span>
                </td>
                <td className="p-3 text-xs">{e.confidence ? (e.confidence*100).toFixed(0)+"%" : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}
